import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
} from "react";

interface TemplateContextType {
  editMode: boolean;
  setEditMode: (mode: boolean) => void;
  onSave: (() => void) | null;
  setOnSave: (fn: (() => void) | null) => void;
  onAddCard?: (fatherId?: string, maxYRepeat?: number) => void;
  setOnAddCard: (
    fn: ((fatherId?: string, maxYRepeat?: number) => void) | undefined,
  ) => void;
  onPreview: (() => void) | null;
  setOnPreview: (fn: (() => void) | null) => void;
  onExport: (() => void) | null;
  setOnExport: (fn: (() => void) | null) => void;
  onDiscard: (() => void) | null;
  setOnDiscard: (fn: (() => void) | null) => void;
  onBack: (() => void) | null;
  setOnBack: (fn: (() => void) | null) => void;
  showBackButton: boolean;
  setShowBackButton: (show: boolean) => void;
  isTemplatePage: boolean;
  setIsTemplatePage: (is: boolean) => void;
  pageTitle: string;
  setPageTitle: (title: string) => void;
  initialPathName: string;
  setInitialPathName: (path: string) => void;
  hasChanges: boolean;
  setHasChanges: (show: boolean) => void;
}

const TemplateContext = createContext<TemplateContextType | undefined>(
  undefined,
);

export const useTemplate = () => {
  const context = useContext(TemplateContext);
  if (!context) {
    throw new Error("useTemplate must be used within a TemplateProvider");
  }
  return context;
};

export const TemplateProvider = ({ children }: { children: ReactNode }) => {
  const [editMode, setEditMode] = useState(false);
  const [onSave, setOnSave] = useState<(() => void) | null>(null);
  const [onAddCard, setOnAddCard] = useState<
    ((fatherId?: string, maxYRepeat?: number) => void) | undefined
  >(undefined);
  const [onPreview, setOnPreview] = useState<(() => void) | null>(null);
  const [onExport, setOnExport] = useState<(() => void) | null>(null);
  const [onDiscard, setOnDiscard] = useState<(() => void) | null>(null);
  const [onBack, setOnBack] = useState<(() => void) | null>(null);
  const [showBackButton, setShowBackButton] = useState(false);
  const [isTemplatePage, setIsTemplatePage] = useState(false);
  const [pageTitle, setPageTitle] = useState("");
  const [initialPathName, setInitialPathName] = useState("");
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  const handleSetInitialPathName = useCallback((path: string) => {
    const pathParts = path.split("/");
    if (pathParts.length > 2) {
      pathParts.pop();
      const basePath = pathParts.join("/");
      setInitialPathName(basePath);
    } else {
      setInitialPathName(path);
    }
  }, []);

  return (
    <TemplateContext.Provider
      value={{
        editMode,
        setEditMode,
        onSave,
        setOnSave,
        onAddCard,
        setOnAddCard,
        onPreview,
        setOnPreview,
        onExport,
        setOnExport,
        onDiscard,
        setOnDiscard,
        onBack,
        setOnBack,
        showBackButton,
        setShowBackButton,
        isTemplatePage,
        setIsTemplatePage,
        pageTitle,
        setPageTitle,
        initialPathName,
        setInitialPathName: handleSetInitialPathName,
        hasChanges,
        setHasChanges,
      }}
    >
      {children}
    </TemplateContext.Provider>
  );
};
